/**
 * MODULE: platform/browser/frontend/utils/browserAssistantOutcome.ts
 * PURPOSE: Classify agent done messages and suggest follow-up commands.
 * DEPENDENCIES: browser.types ChatMessage
 * PUBLIC_API: classifyAssistantOutcome, followUpSuggestionsForOutcome
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: 🟢 SAFE
 * RECENT: 2026-06-17 [2.2.1411] — success / stopped / limit classification for partial-result UX.
 */

import type { ChatMessage } from '../types/browser.types';

export type AssistantOutcome = 'success' | 'stopped' | 'limit';

export function classifyAssistantOutcome(content: string): AssistantOutcome {
  const text = content.trim();
  const lower = text.toLowerCase();
  if (/action limit|reached the \d+-action limit/i.test(text)) {
    return 'limit';
  }
  if (
    /^stopped —/i.test(text) ||
    /did not progress past the same url/i.test(lower) ||
    (/404|not found/i.test(lower) && /stopped|error page/i.test(lower))
  ) {
    return 'stopped';
  }
  return 'success';
}

export function suggestFollowUpCommands(outcome: AssistantOutcome): string[] {
  if (outcome === 'limit') {
    return [
      'Continue from the current page — extract the key details visible now',
      'Scroll down and capture pricing or contact information on this page',
    ];
  }
  if (outcome === 'stopped') {
    return [
      'Navigate directly to the official company website and summarize the homepage',
      'Try a more specific search (company name + "software platform" or "sales intelligence")',
    ];
  }
  return [];
}

export function lastAssistantOutcome(messages: ChatMessage[]): AssistantOutcome | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg.role !== 'assistant') continue;
    return msg.outcome ?? classifyAssistantOutcome(msg.content);
  }
  return null;
}
